import React, { useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import './_sidebar.scss';
import styled, { useTheme } from 'styled-components';
import { NavLink } from 'react-router-dom';
import { SideMenuAccess } from '../../../midleware/midleware';
import { sideMenuState } from '../../../features/auth/authSlice';
import { occyLs } from '../../../utils/localStorage';
import { useDispatch } from 'react-redux';
import AnalyticsIcon from '../../components/svgIcons/AnalyticsIcon';
import HelpIcon from '../../components/svgIcons/QuestionMark';
import HiringRequestIcon from '../../components/svgIcons/HiringRequestIcon';
import { setEventType } from '../../../features/Setting/AccountCalender/AccountSlice';
import { useGetAvailabilityQuery } from '../../../features/Setting/AccountCalender/CalenderApiSlice';
import { USERROLES } from '../../../features/users/creation/assets/utills';
import DashboardIcon from '../../components/svgIcons/DashboardIcon';
import JobsIcon from '../../components/svgIcons/JobsIcon';
import CandidatesIcon from '../../components/svgIcons/CandidatesIcon';
import SettingsIcon from '../../components/svgIcons/SettingsIcon';
import OpenSidebarIcon from '../../components/svgIcons/OpenSidebarIcon';
import CalendarFillIcon from '../../components/svgIcons/CalenderFillIcon';
import SideBarIcon from '../../components/svgIcons/SidebarIcon';
import OfferAgreementDashboardIcon from '../../components/svgIcons/OfferAgreementDashboardIcon';
import OccyLogoWhite from '../../components/svgIcons/OccyLogoWhite';
import {
  PERMISSIONS,
  usePermissions,
  usePermissionActionGuard,
} from '../../../permission-system';
import { canAccessMyRequests } from '../../../features/AuthorityToRecruit/viewConfig';

const MenuIcon = ({ children }) => (
  <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
);

MenuIcon.propTypes = {
  children: PropTypes.node.isRequired,
};

const SideBar = ({ openSideMenu, setOpenSideMenu }) => {
  const { hasWildcard, permissionKeys, enforcing } = usePermissions();
  const hasNavPermission = (permission) => {
    if (!permission) return true;
    if (!enforcing) return false;
    if (hasWildcard) return true;
    return permissionKeys.includes(permission);
  };
  const { data: availability } = useGetAvailabilityQuery();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { can } = usePermissionActionGuard();
  const canAccessMyRequestsRoute = canAccessMyRequests(can);

  useEffect(() => {
    if (availability) {
      dispatch(
        setEventType(availability?.autoAvailability === false ? 2 : 1),
      );
    }
  }, [availability, dispatch]);

  const sidebarVisible = !!occyLs.getObject('APP_SIDEBAR');
  const MenuItems = [
    {
      name: 'Dashboard',
      icon: (
        <MenuIcon>
          <DashboardIcon />
        </MenuIcon>
      ),
      to: 'dashboard',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.INTERVIEWER,
        USERROLES.HIRINGMANAGER,
        USERROLES.BILLINGADMIN,
        USERROLES.APPROVER,
      ],
      childUser: true,
    },
    {
      name: 'Jobs',
      icon: (
        <MenuIcon>
          <JobsIcon />
        </MenuIcon>
      ),
      to: 'jobs',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
        USERROLES.INTERVIEWER,
      ],
      childUser: false,
    },
    {
      name: 'Hiring Requests',
      icon: (
        <MenuIcon>
          <HiringRequestIcon />
        </MenuIcon>
      ),
      to: 'authority-to-recruit/inbox',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
        USERROLES.INTERVIEWER,
      ],
      childUser: false,
    },
    {
      name: 'My Requests',
      icon: (
        <MenuIcon>
          <HiringRequestIcon />
        </MenuIcon>
      ),
      to: 'authority-to-recruit/my-requests',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
        USERROLES.INTERVIEWER,
      ],
      childUser: false,
      hidden: !canAccessMyRequestsRoute,
    },
    {
      name: 'Offers & Agreements',
      icon: (
        <MenuIcon>
          <OfferAgreementDashboardIcon />
        </MenuIcon>
      ),
      to: 'offers-and-agreements',
      authorizedRoles: [USERROLES.ADMIN, USERROLES.RECRUITER],
      requiredPermission: PERMISSIONS.MANAGE_DOCUMENTS,
      nobasicAllowed: true,
    },
    {
      name: 'Candidates',
      icon: (
        <MenuIcon>
          <CandidatesIcon sideColor={theme?.secondary} size={23} />
        </MenuIcon>
      ),
      to: 'candidates',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.HIRINGMANAGER,
      ],
      nobasicAllowed: true,
    },
    {
      name: 'Calendar',
      icon: (
        <MenuIcon>
          <CalendarFillIcon />
        </MenuIcon>
      ),
      to: 'calendar',
      authorizedRoles: [
        USERROLES.ADMIN,
        USERROLES.RECRUITER,
        USERROLES.INTERVIEWER,
        USERROLES.HIRINGMANAGER,
      ],
    },
    {
      name: 'Analytics',
      icon: (
        <MenuIcon>
          <AnalyticsIcon />
        </MenuIcon>
      ),
      to: 'analytics',
      authorizedRoles: [USERROLES.ADMIN, USERROLES.RECRUITER],
      requiredPermission: PERMISSIONS.MANAGE_REPORT_ANALYTICS,
      nobasicAllowed: true,
    },
    {
      name: 'Settings',
      icon: (
        <MenuIcon>
          <SettingsIcon size="24px" />
        </MenuIcon>
      ),
      to: 'settings',
      childUser: true,
    },
    {
      name: 'Help',
      icon: (
        <MenuIcon>
          <HelpIcon size="24px" />
        </MenuIcon>
      ),
      to: '',
      action: () => {
        openWidget();
      },
    },
  ];

  const openWidget = () => {
    globalThis.FreshworksWidget('open');
  };

  const getActiveColorClass = (isActive, itemName) => {
    const highlightable =
      isActive && itemName !== 'Help' && itemName !== 'University';
    if (!highlightable) return 'tab-link';
    if (theme.dark) return 'text-headingColor';
    return 'text-white';
  };

  const toggleSidebar = () => {
    dispatch(sideMenuState(!sidebarVisible));
    setOpenSideMenu(!openSideMenu);
  };

  return (
    <SideBarContainer
      className={` ${
        sidebarVisible ? 'openSidebar' : 'closeSidebar'
      } vh-100  p-0 position-fixed `}
    >
      <div
        className={`${
          sidebarVisible
            ? 'd-flex justify-content-between align-items-center px-4 py-4'
            : 'justify-content-center d-flex pt-4 pb-4'
        } `}
      >
        {sidebarVisible ? <OccyLogoWhite /> : null}
        <button
          type="button"
          className="menuButton p-2 rounded-circle cursor-pointer"
          aria-label={sidebarVisible ? 'Collapse sidebar' : 'Expand sidebar'}
          onClick={toggleSidebar}
        >
          {sidebarVisible ? <OpenSidebarIcon /> : <SideBarIcon />}
        </button>
      </div>
      <svg
        width={`${sidebarVisible ? '200' : '50'}`}
        height="50"
        viewBox="0 0 400 20"
        xmlns="http://www.w3.org/2000/svg"
        className={sidebarVisible ? 'mx-4 pb-1 ' : 'mx-2 pb-1'}
      >
        <defs>
          <pattern
            id="wavePattern"
            x="0"
            y="0"
            width="40"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M0,10 c10,-10 30,10 40,0"
              fill="none"
              stroke="#dc601e"
              strokeWidth={`${sidebarVisible ? '2' : '10'}`}
              strokeLinecap="round"
            />
          </pattern>
        </defs>
        <rect x="0" y="0" width="400" height="20" fill="url(#wavePattern)">
          <animateTransform
            attributeName="transform"
            type="translate"
            from="0,0"
            to="-40,0"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>
      </svg>

      <StyledSideMenu>
        {MenuItems.map((item) => {
          if (
            item?.requiredPermission &&
            !hasNavPermission(item.requiredPermission)
          ) {
            return null;
          }
          const isMenuVisible = SideMenuAccess(item) && !item.hidden;
          if (!isMenuVisible) return null;

          return (
            <NavLink
              key={item.to || item.name}
              target={item?.name === 'University' ? '_blank' : undefined}
              to={item?.name === 'Help' ? undefined : item?.to}
              className={({ isActive }) => {
                const highlightable =
                  isActive &&
                  item?.name !== 'Help' &&
                  item?.name !== 'University';
                const layoutClass = sidebarVisible
                  ? 'd-flex align-items-center px-4 ms-0'
                  : 'justify-content-center d-flex py-4 mx-1';
                return `hoverEffect ${
                  highlightable ? 'bg-hightlightedBar' : ''
                } ${getActiveColorClass(isActive, item?.name)} ${layoutClass} text-decoration-none space `;
              }}
              onClick={(e) => {
                if (
                  item?.requiredPermission &&
                  !hasNavPermission(item.requiredPermission)
                ) {
                  e.preventDefault();
                  return;
                }
                item?.action?.(e);
              }}
            >
              <div>{item.icon}</div>
              {sidebarVisible ? (
                <p className="ms-3 mt-1 MenuText mb-0">{item.name}</p>
              ) : null}
            </NavLink>
          );
        })}
      </StyledSideMenu>
    </SideBarContainer>
  );
};

SideBar.propTypes = {
  openSideMenu: PropTypes.bool,
  setOpenSideMenu: PropTypes.func,
};

const StyledSideMenu = styled.div`
  .tab-link {
    color: ${({ theme }) => theme.bgPebble};
  }
  .space {
    padding: 20px 0 20px 0;
  }
  max-height: 80%;
  overflow: auto;
  position: relative;
  @-moz-document url-prefix() {
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.primary} transparent;
  }
  ::-webkit-scrollbar-thumb,
  ::-webkit-scrollbar-track,
  ::-webkit-scrollbar-thumb:hover,
  ::-webkit-scrollbar {
    background: ${({ theme }) => theme.primary};
  }

  .hoverEffect {
    position: relative;
    color: white;
    overflow: hidden;
    z-index: 1;
    transition: color 0.3s ease;

    &:hover {
      color: white;
      text-shadow: 0 0 2px rgba(255, 255, 255, 0.8);
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      width: 0;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.08);
      transition: width 0.4s ease;
      z-index: -1;
    }

    &:hover::before {
      width: 100%;
    }
  }
`;

const SideBarContainer = styled.div`
  background: ${({ theme }) => theme?.sideBar} !important;
`;

export default SideBar;
